<template>
  <li class="profile-nav pe-0 py-0" ref="profileRoot">
    <button class="media profile-media profile-trigger-card" :class="{ 'is-icon-only': iconOnly }" type="button" @click.stop="toggleProfileDropdown">
      <div v-if="iconOnly" class="profile-trigger-icon" aria-hidden="true">
        <i class="fa fa-cog"></i>
      </div>
      <div v-else class="media-body profile-trigger-content">
        <span>{{ userNameDisplay }}</span>
        <p class="mb-0 font-roboto">
          {{ userEmailDisplay }} <i class="middle fa fa-angle-down"></i>
        </p>
      </div>
    </button>
    <ul class="profile-dropdown profile-click-dropdown" v-if="showProfileDropdown">
      <li class="profile-dropdown-header">
        <span class="profile-dropdown-title">{{ profileMenuTitle }}</span>
      </li>
      <li class="profile-menu-item">
        <router-link :to="{ name: 'settings', query: { section: 'account' } }">
          <span class="profile-option-type">Mi Cuenta</span>
          <small class="profile-option-detail">Administra tu información personal</small>
        </router-link>
      </li>
      <li class="profile-menu-item">
        <router-link :to="{ name: 'settings', query: { section: 'billing' } }">
          <span class="profile-option-type">Facturación</span>
          <small class="profile-option-detail">Ingresa tus datos fiscales</small>
        </router-link>
      </li>
      <li class="profile-menu-item">
        <router-link :to="{ name: 'settings', query: { section: 'subscription' } }">
          <span class="profile-option-type">Suscripción</span>
          <small class="profile-option-detail">Administra tu plan y pagos</small>
        </router-link>
      </li>
      <li class="profile-menu-item">
        <router-link :to="{ name: 'settings', query: { section: 'rfcs' } }">
          <span class="profile-option-type">Mis RFCs</span>
          <small class="profile-option-detail">Consulta y administra tus RFC registrados</small>
        </router-link>
      </li>
      <li class="profile-menu-item">
        <router-link :to="{ name: 'settings', query: { section: 'password' } }">
          <span class="profile-option-type">Contraseña</span>
          <small class="profile-option-detail">Actualiza tu contraseña</small>
        </router-link>
      </li>
      <li v-if="impersonationActive" class="profile-menu-item">
        <a @click="restoreAdminSession">
          <span class="profile-option-type">Volver a sesión admin</span>
          <small class="profile-option-detail">Regresa a tu sesión original de administrador</small>
        </a>
      </li>
      <li class="profile-menu-divider" role="separator"></li>
      <li class="profile-menu-item">
        <a @click="logout">
          <span class="profile-option-type">Cerrar sesión</span>
          <small class="profile-option-detail">Salir de tu cuenta actual</small>
        </a>
      </li>
    </ul>
  </li>
</template>

<script>

  export default {
    name: 'Profile',
    props: {
      iconOnly: {
        type: Boolean,
        default: false,
      },
    },
    data() {
    return {
      userNameValue: '',
      userEmailValue: '',
      showProfileDropdown: false,
      profileMenuTitle: 'Configuración',
      impersonationActive: false,
    };
  },
    computed: {
      userNameDisplay() {
        const userName = String(this.userNameValue || '').trim();
        return userName || 'Usuario sin nombre';
      },
      userEmailDisplay() {
        const userEmail = String(this.userEmailValue || '').trim();
        return userEmail || 'Sin correo';
      }
    },
    mounted() {
      this.loadUserInfo();

      document.addEventListener('click', this.closeProfileDropdown);
      window.addEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
      window.addEventListener('storage', this.handleStorageEvent);
    },
    beforeDestroy() {
      document.removeEventListener('click', this.closeProfileDropdown);
      window.removeEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
      window.removeEventListener('storage', this.handleStorageEvent);
    },
    methods: {
      readBackupSession() {
        const rawBackup = localStorage.getItem('admin_impersonation_backup');
        if (!rawBackup || rawBackup === 'undefined') {
          return null;
        }

        try {
          return JSON.parse(rawBackup) || null;
        } catch (_) {
          return null;
        }
      },
      refreshImpersonationState() {
        const backup = this.readBackupSession();
        const currentToken = String(localStorage.getItem('access_token') || '').trim();
        const backupToken = String(backup?.admin_token || '').trim();
        this.impersonationActive = Boolean(backup && backupToken && currentToken && currentToken !== backupToken);
      },
      handleStorageEvent(event) {
        if (!event || event.key === null || ['access_token', 'user', 'admin_impersonation_backup'].includes(event.key)) {
          this.loadUserInfo();
        }
      },
      emitHeaderDropdownOpen(source) {
        window.dispatchEvent(new CustomEvent('header-dropdown-open', { detail: { source } }));
      },
      handleHeaderDropdownOpen(event) {
        const source = event && event.detail ? event.detail.source : '';

        if (source !== 'profile') {
          this.showProfileDropdown = false;
        }
      },
      toggleProfileDropdown() {
        this.showProfileDropdown = !this.showProfileDropdown;

        if (this.showProfileDropdown) {
          this.emitHeaderDropdownOpen('profile');
        }
      },
      closeProfileDropdown(event) {
        if (!this.showProfileDropdown) {
          return;
        }

        const root = this.$refs.profileRoot;
        if (root && root.contains(event.target)) {
          return;
        }

        this.showProfileDropdown = false;
      },
      loadUserInfo() {
        const rawUser = localStorage.getItem('user');

        if (!rawUser || rawUser === 'undefined') {
          this.userNameValue = '';
          this.userEmailValue = '';
          this.impersonationActive = false;
          return;
        }

        try {
          const user = JSON.parse(rawUser) || {};
          this.userNameValue = String(user.name || '').trim();
          this.userEmailValue = String(user.email || '').trim();
        } catch (error) {
          this.userNameValue = '';
          this.userEmailValue = '';
        }

        this.refreshImpersonationState();
      },
      restoreAdminSession() {
        const backup = this.readBackupSession();
        if (!backup) {
          return;
        }

        const adminToken = String(backup.admin_token || '').trim();
        const adminUser = backup.admin_user || null;
        if (!adminToken || !adminUser) {
          return;
        }

        localStorage.setItem('access_token', adminToken);
        localStorage.setItem('user', JSON.stringify(adminUser));
        localStorage.setItem('rfcs', String(backup.admin_rfcs || '[]'));
        localStorage.setItem('rfc', String(backup.admin_rfc || ''));
        localStorage.setItem('fiscal_name', String(backup.admin_fiscal_name || ''));
        localStorage.setItem('period', String(backup.admin_period || ''));
        localStorage.removeItem('admin_impersonation_backup');
        localStorage.removeItem('admin_impersonation_context');

        this.loadUserInfo();
        this.showProfileDropdown = false;
        if (this.$toast && this.$toast.success) {
          this.$toast.success('Sesión de administrador restaurada.');
        }
        this.$router.push({ name: 'adminAccounts' }).catch(() => {});
      },
      logout: function() {
        this.$router.replace('/login');
        localStorage.removeItem('user');
        localStorage.removeItem('access_token');
        localStorage.removeItem('admin_impersonation_backup');
        localStorage.removeItem('admin_impersonation_context');
      }
    },
  };
</script>

<style scoped>
.profile-nav {
  position: relative;
  overflow: visible;
}

.profile-trigger-card {
  border: 1px solid var(--sidebar-border);
  border-radius: 8px;
  background: var(--white);
  padding: 8px 15px 7px;
  height: 52px;
  min-height: 52px;
  width: auto;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
}

.profile-trigger-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px var(--sidebar-border);
}

.profile-trigger-content {
  text-align: right;
  margin-left: 0px !important;
}

.profile-trigger-content > span {
  display: block;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.15;
  color: var(--theme-font-color);
}

.profile-trigger-content p {
  margin: 2px 0 0 0;
  font-size: 11px;
  color: var(--theme-font-color);
  opacity: 0.82;
}

.profile-trigger-content .middle {
  margin-left: 4px;
}

.profile-trigger-icon {
  width: 20px;
  text-align: center;
  color: var(--theme-font-color);
}

.profile-trigger-icon i {
  font-size: 16px;
}

.profile-trigger-card.is-icon-only {
  width: 42px;
  min-width: 42px;
  padding: 0;
  justify-content: center;
}

.profile-click-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0 !important;
  left: auto !important;
  min-width: 260px;
  max-width: calc(100vw - 16px);
  margin: 0;
  padding: 0;
  padding-left: 0 !important;
  padding-right: 0 !important;
  list-style: none;
  border: 1px solid var(--sidebar-border);
  border-radius: 8px;
  background-color: var(--white) !important;
  box-shadow: 0 6px 18px var(--sidebar-border);
  z-index: 12;
  opacity: 1 !important;
  visibility: visible !important;
  transform: translateY(0) !important;
  transform-origin: top right;
  backdrop-filter: none;
}

.profile-click-dropdown > li {
  margin: 0 !important;
  padding: 0 !important;
  list-style: none;
}

.profile-click-dropdown .profile-menu-item {
  margin: 0 !important;
  padding: 0 !important;
}

.profile-click-dropdown .profile-menu-item > a {
  margin: 0 !important;
  padding: 8px 12px !important;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 2px;
}

.profile-click-dropdown > li > a {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 5px 12px;
  text-decoration: none;
  cursor: pointer;
  color: var(--theme-font-color);
  transition: background-color 0.14s ease, color 0.14s ease;
}

.profile-click-dropdown .profile-menu-item + .profile-menu-item > a {
  border-top: 1px solid var(--sidebar-border);
}

.profile-click-dropdown .profile-menu-item + .profile-menu-divider,
.profile-click-dropdown .profile-menu-divider + .profile-menu-item {
  border-top: 0;
}

.profile-click-dropdown .profile-menu-item > a:hover {
  background-color: var(--sidebar-border);
}

.profile-click-dropdown .profile-option-type {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: var(--theme-font-color);
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-click-dropdown .profile-option-detail {
  display: block;
  font-size: 10px;
  color: var(--theme-font-color);
  opacity: 0.78;
  line-height: 1.2;
  margin: 0;
}

.profile-click-dropdown .profile-menu-item > a:hover .profile-option-type {
  color: var(--theme-deafult);
}

.profile-click-dropdown .profile-menu-divider {
  height: 1px;
  margin: 0 !important;
  background-color: var(--sidebar-border);
}

.profile-click-dropdown .profile-dropdown-header {
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 0 !important;
  padding: 10px 12px !important;
  border-bottom: 1px solid var(--sidebar-border);
  background: linear-gradient(100deg, rgba(247, 49, 100, 0.06) 0%, rgba(115, 102, 255, 0.08) 100%);
}

.profile-click-dropdown .profile-dropdown-title {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--theme-deafult);
  letter-spacing: 0.02em;
}
</style>
